/**
 * report — the feedback seam (measurement addendum §3).
 *
 * Features call ONLY {@link report}; they never talk to a feedback backend
 * directly. That keeps one swap point for the sink.
 *
 * SINK TODAY: a local stub — persists the last N reports to `localStorage` and
 * logs them. There is no Shake *web* SDK in this repo (grounded: no `@shakebugs`
 * dependency anywhere), so we do NOT add one speculatively.
 *
 * SWAP TARGET (the future homebrew sink): a backend **Feedback** entity written
 * via the GraphQL API + **Botler** capture (screenshot/session enrichment). When
 * that lands, or if a Shake web SDK is adopted, replace {@link reportSink} here —
 * one file, call sites unchanged. See `TRACKING-PLAN.md` (Report Filed event).
 */
import { track } from "@/lib/analytics";

export type ReportCategory = "bug" | "confusing" | "idea" | "other";

/** Everything a report carries. `screenshotRef` is optional (Botler fills it later). */
export interface ReportContext {
  /** Where in the product, e.g. "theming" | "members". */
  surface: string;
  /** The route path, e.g. "/b/nova/theming". */
  route: string;
  /** Active brand id, when there is one. */
  brandId: string | null;
  /** Opaque session id (best-effort). */
  session: string;
  /** Reporter identity (username/displayName), when signed in. */
  user: string;
  category: ReportCategory;
  /** Free-text detail from the reporter. */
  message?: string;
  /** A pointer to a screenshot/asset (future: Botler capture). */
  screenshotRef?: string;
}

export interface StoredReport extends ReportContext {
  id: string;
  ts: number;
}

const STORAGE_KEY = "popin.portal.reports";
const MAX_STORED = 50;

function loadStored(): StoredReport[] {
  try {
    const raw = globalThis.localStorage?.getItem(STORAGE_KEY);
    return raw ? (JSON.parse(raw) as StoredReport[]) : [];
  } catch {
    return [];
  }
}

/** The current sink. Swap this to route reports to the homebrew backend / Shake. */
let reportSink = (r: StoredReport): void => {
  try {
    const all = [r, ...loadStored()].slice(0, MAX_STORED);
    globalThis.localStorage?.setItem(STORAGE_KEY, JSON.stringify(all));
  } catch {
    /* storage may be unavailable (SSR / private mode) — logging below still runs */
  }
  // eslint-disable-next-line no-console
  console.info("[report] filed", r);
};

export function setReportSink(next: (r: StoredReport) => void): void {
  reportSink = next;
}

/** File a report. Emits the single "Report Filed" analytics event (via the seam). */
export function report(ctx: ReportContext): StoredReport {
  const stored: StoredReport = {
    ...ctx,
    id: `rpt_${Date.now().toString(36)}_${Math.floor(Math.random() * 1e6).toString(36)}`,
    ts: Date.now(),
  };
  reportSink(stored);
  track("Report Filed", {
    surface: ctx.surface,
    route: ctx.route,
    brandId: ctx.brandId,
    category: ctx.category,
    hasScreenshot: !!ctx.screenshotRef,
  });
  return stored;
}

/** The reports persisted locally (for a future "my reports" view / debugging). */
export function storedReports(): StoredReport[] {
  return loadStored();
}
